/**
 * Fetch-all helper for limit/offset list endpoints.
 *
 * The control-plane list endpoints clamp ?limit to 200 (httpx.LimitOffset). A
 * few god-view lists must show EVERY row no matter how many exist — the
 * platform console's orgs + users, and the vendor portal's customer fleet. A
 * single capped request silently hides everything past the first page (the
 * 84-org / 50-row bug), so for those we page through with ?limit=200&offset=N
 * and concatenate. Correct for arbitrarily many rows, not just the current set.
 */

import { api } from "@/lib/api-client";
import type { Paginated } from "./types";

/** The server's max page size — httpx.LimitOffset clamps ?limit to 200. */
const MAX_PAGE = 200;

/**
 * Page through a limit/offset list endpoint and return every row in one
 * Paginated envelope. Loops offset in MAX_PAGE steps until a page comes back
 * short (the last page). `path` may already carry a query string; the
 * limit/offset params are appended with the correct separator. The returned
 * `total` is the TRUE row count — these endpoints' own `total` only reports the
 * current page's length (Total: len(items)), so it can't be trusted for "N of M".
 * `options.orgId` is sent as the X-Org-Id header (api.get's per-call org).
 */
export async function fetchAllPages<T>(path: string, options?: { orgId?: string }): Promise<Paginated<T>> {
  const sep = path.includes("?") ? "&" : "?";
  const items: T[] = [];
  let offset = 0;
  let pageLen = 0;
  do {
    const url = `${path}${sep}limit=${MAX_PAGE}&offset=${offset}`;
    const page = options?.orgId
      ? await api.get<Paginated<T>>(url, { orgId: options.orgId })
      : await api.get<Paginated<T>>(url);
    const batch = page.items ?? [];
    items.push(...batch);
    pageLen = batch.length;
    offset += MAX_PAGE;
  } while (pageLen === MAX_PAGE);
  return { items, total: items.length, limit: items.length, offset: 0 };
}
